/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'
import { defineMessages, useIntl } from 'react-intl'

import {
  EUI_MODAL_CANCEL_BUTTON,
  EuiConfirmModal,
  EuiIcon,
  EuiOverlayMask,
  EuiProgress,
} from '@elastic/eui'

import type { InstanceConfiguration, MetadataItem } from '@modules/cloud-api/v1/types'
import type { RegionId } from '@modules/ui-types'
import PermissionsGate from '@modules/permissions-components/PermissionsGate'

import RemovableAllocatorTagModalBody from './RemovableAllocatorTagModalBody/RemovableAllocatorTagModalBody'
import AllocatorTag from './AllocatorTag'

interface InstanceWithConfiguration {
  clusterId: string
  id: string
  instanceName: string
  capacity: number
  kind: string
  instanceConfiguration: InstanceConfiguration
}

interface Props {
  modal?: any
  allocatorId: string
  tag: MetadataItem
  spin: boolean
  regionId: RegionId
  instances: InstanceWithConfiguration[]
  onRemove: () => void
}

const messages = defineMessages({
  removeTag: {
    id: `allocator-tag-modal.modal-title`,
    defaultMessage: `Remove allocator tag?`,
  },
  cancel: {
    id: `allocator-tag-modal.modal-cancel`,
    defaultMessage: `Cancel`,
  },
  remove: {
    id: `allocator-tag-modal.modal-confirm`,
    defaultMessage: `Remove`,
  },
})

const RemovableAllocatorTag = ({
  tag,
  spin,
  allocatorId,
  regionId,
  instances,
  modal,
  onRemove,
}: Props) => {
  const [isModalOpen, setIsModalOpen] = useState(false)
  const { formatMessage } = useIntl()

  const showModal = () => setIsModalOpen(true)
  const closeModal = () => setIsModalOpen(false)

  const confirmAction = () => {
    closeModal()
    onRemove()
  }

  const renderModal = () => {
    if (!isModalOpen) {
      return null
    }

    const defaultBody = (
      <RemovableAllocatorTagModalBody instances={instances} regionId={regionId} tag={tag} />
    )

    const {
      title = formatMessage(messages.removeTag),
      cancelButtonText = formatMessage(messages.cancel),
      confirmButtonText = formatMessage(messages.remove),
      defaultFocusedButton = EUI_MODAL_CANCEL_BUTTON,
      className = ``,
      body = defaultBody,
    } = modal || {}

    return (
      <EuiOverlayMask>
        <EuiConfirmModal
          className={className}
          title={title}
          cancelButtonText={cancelButtonText}
          confirmButtonText={confirmButtonText}
          defaultFocusedButton={defaultFocusedButton}
          onCancel={closeModal}
          onConfirm={confirmAction}
        >
          {body}
        </EuiConfirmModal>
      </EuiOverlayMask>
    )
  }

  return (
    <PermissionsGate
      permissions={[
        {
          type: 'allocator',
          action: 'delete',
          id: allocatorId,
        },
      ]}
    >
      {({ hasPermissions }) => (
        <Fragment>
          <AllocatorTag
            disabled={!hasPermissions}
            style={{ position: 'relative', overflow: 'hidden' }}
            tag={tag}
            onClick={showModal}
            color='hollow'
          >
            <EuiIcon type='cross' style={{ marginLeft: '0.125rem' }} />

            {spin && <EuiProgress color='danger' size='xs' position='absolute' />}
          </AllocatorTag>

          {hasPermissions && renderModal()}
        </Fragment>
      )}
    </PermissionsGate>
  )
}

export default RemovableAllocatorTag
